import {ofType} from 'redux-observable';
import {Observable, of} from 'rxjs';
import i18next from 'i18next';
import {mergeMap, catchError, map} from 'rxjs/operators';

import {combineEpics} from 'web-app/util/redux-observable';
import * as AccountItemActions from 'web-app/react/entities/account-items/actions';
import {showDialogObservable, showDeleteDialogObservable, DialogChoice} from 'web-app/react/dialog';
import * as GlobalEventActions from 'web-app/react/global-event/actions';

import * as Api from './api';
import * as Actions from './actions';
import * as Helpers from './helpers';

export const fetchBillPayerBalanceObservable = (
    institutionId,
    billPayerId,
    filterCreditedInvoices,
    limit,
    expectedIds,
    unexpectedIds,
) => {
    return Api.fetchBalance(
        institutionId,
        billPayerId,
        filterCreditedInvoices,
        undefined,
        limit,
        expectedIds,
        unexpectedIds,
    ).map(invoicing => Actions.fetchBillPayerBalanceSuccess.action({billPayerId, invoicing, limit}));
};

export const previewInvoiceObservable = (institutionId, billPayerId, invoice, options) => {
    return Api.previewInvoice(institutionId, billPayerId, invoice, options).map(result =>
        AccountItemActions.previewInvoiceSuccess.action({result}),
    );
};

const fetchBillPayerBalance = action$ =>
    action$
        .ofType(Actions.fetchBillPayerBalance.type)
        .mergeMap(({payload}) =>
            fetchBillPayerBalanceObservable(
                payload.institutionId,
                payload.billPayerId,
                payload.filterCreditedInvoices,
                payload.limit,
                payload.expectedIds,
                payload.unexpectedIds,
            ).catch(e => Observable.of(Actions.fetchBillPayerBalanceFailed.action(e))),
        );

export const fetchPaymentsForTargetObservable = (
    targetId,
    offset,
    limit,
    from,
    to,
    confirmedFilter,
    paymentMethod,
    deposited,
    transactionStatus,
    createdAtFrom,
    createdAtTo,
    includeDeleted,
    billPayerIds,
    sortBy,
    sortAscending,
) =>
    Api.fetchPaymentsForTarget(
        targetId,
        offset,
        limit,
        from,
        to,
        confirmedFilter,
        paymentMethod,
        deposited,
        transactionStatus,
        createdAtFrom,
        createdAtTo,
        includeDeleted,
        billPayerIds,
        sortBy,
        sortAscending,
    ).map(data =>
        Actions.fetchPaymentsForTargetSuccess.action({
            ...data,
            offset,
            targetId,
        }),
    );

export const fetchInvoicesForTargetObservable = (
    targetId,
    offset,
    limit,
    from,
    to,
    createdAtFrom,
    createdAtTo,
    billPayerIds,
) =>
    Api.fetchInvoicesForTarget(targetId, offset, limit, from, to, createdAtFrom, createdAtTo, billPayerIds).map(
        data => {
            return Actions.fetchInvoicesForTargetSuccess.action({
                ...data,
                offset,
                targetId,
            });
        },
    );

export const addPaymentObservable = (institutionId, billPayerId, payment) =>
    Api.createPayment(institutionId, billPayerId, payment).mergeMap(result =>
        Observable.merge(
            Observable.of(
                Actions.createPaymentSuccess.action({
                    institutionId,
                    billPayerId,
                    newPayment: result.payment,
                }),
            ),
            Observable.of(
                GlobalEventActions.updateSuccess.action(
                    i18next.t('childProfile.accountBalance.paymentCreationSuccessful'),
                ),
            ),
        ),
    );

const addPayment = action$ =>
    action$
        .ofType(Actions.createPayment.type)
        .mergeMap(({payload}) =>
            addPaymentObservable(payload.institutionId, payload.billPayerId, payload.payment).catch(e =>
                Observable.of(Actions.createPaymentFailed.action(e)),
            ),
        );

export const updatePaymentObservable = (institutionId, billPayerId, payment, oldPayment) =>
    Api.updatePayment(institutionId, billPayerId, payment.id, payment).map(result =>
        Actions.updatePaymentSuccess.action({newPayment: result.payment, oldPayment, billPayerId}),
    );

const updatePayment = action$ =>
    action$
        .ofType(Actions.updatePayment.type)
        .mergeMap(({payload}) =>
            updatePaymentObservable(
                payload.institutionId,
                payload.billPayerId,
                payload.payment,
                payload.oldPayment,
            ).catch(e => Observable.of(Actions.updatePaymentFailed.action(e))),
        );

const deletePayment = action$ =>
    action$.ofType(Actions.deletePayment.type).mergeMap(({payload}) =>
        showDeleteDialogObservable({
            title: '',
            description: i18next.t('childProfile.accountBalance.confirmPaymentDelete'),
        }).mergeMap(selection => {
            if (selection === DialogChoice.OK) {
                return Api.deletePayment(payload.institutionId, payload.billPayerId, payload.payment.id)
                    .map(result =>
                        Actions.deletePaymentSuccess.action({
                            result,
                            oldPayment: payload.payment,
                            institutionId: payload.institutionId,
                            billPayerId: payload.billPayerId,
                        }),
                    )
                    .catch(e => Observable.of(Actions.deletePaymentFailed.action(e)));
            } else {
                return Observable.of(Actions.deletePaymentAborted.action());
            }
        }),
    );

const addInvoice = action$ =>
    action$.pipe(
        ofType(Actions.createInvoice.type),
        mergeMap(({payload}) => {
            return showDialogObservable(Helpers.addInvoiceDialogTexts(payload?.invoice?.notify)).pipe(
                mergeMap(selection => {
                    if (selection === DialogChoice.OK) {
                        return Api.createInvoice(payload.institutionId, payload.billPayerId, payload.invoice).pipe(
                            map(result =>
                                AccountItemActions.createInvoiceSuccess.action({
                                    invoices: result.invoices,
                                    billPayerId: payload.billPayerId,
                                    institutionId: payload.institutionId,
                                }),
                            ),
                            catchError(e => of(Actions.createInvoiceFailed.action(e))),
                        );
                    } else {
                        return of(Actions.createInvoiceAborted.action());
                    }
                }),
            );
        }),
    );

const addInvoiceWithGraphQL = action$ =>
    action$.pipe(
        ofType(Actions.createInvoiceWithGraphQL.type),
        mergeMap(({payload}) => {
            return showDialogObservable(Helpers.addInvoiceDialogTexts(payload?.invoiceData?.notify)).pipe(
                mergeMap(selection => {
                    if (selection === DialogChoice.OK) {
                        return Api.createInvoiceInApollo(payload.invoiceData).pipe(
                            map(result =>
                                AccountItemActions.createInvoiceWithGraphQLSuccess.action({
                                    invoices: result.invoices,
                                    billPayerId: payload.billPayerId,
                                    institutionId: payload.institutionId,
                                }),
                            ),
                            catchError(e => of(Actions.createInvoiceWithGraphQLFailed.action(e))),
                        );
                    } else {
                        return of(Actions.createInvoiceWithGraphQLAborted.action());
                    }
                }),
            );
        }),
    );

const previewInvoice = action$ =>
    action$
        .ofType(Actions.previewInvoice.type)
        .mergeMap(({payload}) =>
            previewInvoiceObservable(payload.institutionId, payload.billPayerId, payload.invoice).catch(e =>
                Observable.of(Actions.previewInvoiceFailed.action(e)),
            ),
        );

export default combineEpics(
    fetchBillPayerBalance,
    addPayment,
    updatePayment,
    deletePayment,
    addInvoice,
    addInvoiceWithGraphQL,
    previewInvoice,
);
